<template>
  <v-menu
    v-if="!mobile"
    bottom
    offset-y
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    transition="scale-transition"
    max-width="290px"
    min-width="auto"
    v-bind="menuProps"
    :disabled="!isEditable"
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        ref="dataField"
        v-model="dateInput"
        v-mask="mask"
        :hint="'hint' in $attrs ? $attrs.hint : 'yyyy-mm-dd'"
        v-bind="$attrs"
        :rules="
          'rules' in $attrs && Array.isArray($attrs.rules)
            ? $attrs.rules.concat(dateRules)
            : dateRules
        "
        max="9999-12-31"
        v-on="on"
        :clearable="clearable && isEditable"
        :label="label"
        :readonly="!isEditable"
        @keydown.enter="menu = false"
      >
        <template v-slot:append>
          <slot name="append">
            <v-row
              no-gutters
              justify="center"
              align="center"
              class="flex-nowrap"
            >
              <v-col cols="auto">
                <v-icon>mdi-calendar</v-icon>
              </v-col>
            </v-row>
          </slot>
        </template>
        <template #prepend-inner>
          <slot name="prepend-inner"></slot>
        </template>
      </v-text-field>
    </template>
    <v-date-picker
      v-model="dateValue"
      no-title
      color="primary"
      @change="closeOnDubleClick"
      :max="
        futureDate
          ? '9999-12-31'
          : new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
              .toISOString()
              .substring(0, 10)
      "
    >
      <template v-slot:default>
        <slot
          name="menu-actions"
          :close="close"
          :save="
            () => {
              menu = false;
            }
          "
        >
          <v-btn small @click="close" style="text-transform: none!important;"
            >{{ $t("close") }}
          </v-btn>
          <v-spacer />
          <v-btn
            small
            color="success"
            @click="menu = false"
            style="text-transform: none!important;"
            >{{ $t("save") }}
          </v-btn>
        </slot>
      </template>
    </v-date-picker>
  </v-menu>
  <div v-else>
    <v-text-field
      ref="dataField"
      v-model="dateInput"
      v-mask="mask"
      class="mb-1"
      :hint="'hint' in $attrs ? $attrs.hint : 'yyyy-mm-dd'"
      v-bind="$attrs"
      :rules="
        'rules' in $attrs && Array.isArray($attrs.rules)
          ? $attrs.rules.concat(dateRules)
          : dateRules
      "
      max="9999-12-31"
      v-on="on"
      :clearable="clearable && isEditable"
      :label="label"
      :readonly="!isEditable"
    >
      <template v-slot:append>
        <slot name="append">
          <v-row no-gutters justify="center" align="center" class="flex-nowrap">
            <v-col cols="auto">
              <v-icon>mdi-calendar</v-icon>
            </v-col>
          </v-row>
        </slot>
      </template>
      <template #prepend-inner>
        <slot name="prepend-inner"></slot>
      </template>
    </v-text-field>
    <v-row no-gutters justify="center">
      <v-col cols="auto">
        <v-date-picker
          v-model="dateValue"
          no-title
          color="primary"
          :max="
            futureDate
              ? '9999-12-31'
              : new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                  .toISOString()
                  .substring(0, 10)
          "
        />
      </v-col>
    </v-row>
  </div>
</template>
<script>
import { defineComponent } from "vue";
import moment from "moment";

export default defineComponent({
  name: "TcnDatePicker",
  props: {
    menuProps: Object,
    clearable: {
      type: Boolean,
      default: true,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
    value: String,
    label: String,
    readonly: Boolean,
    futureDate: {
      type: Boolean,
      default: false,
    },
    closeOnFirstClick: {
      type: Boolean,
      default: false,
    },
    format: {
      type: String,
      default: "YYYY-MM-DD",
    },
    mask: {
      type: String,
      default: "####-##-##",
    },
    mandatory: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      dateRegex: /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/,
      menu: false,
      dateRules: [],
    };
  },
  computed: {
    dateValue: {
      get() {
        return this.value;
      },
      set(val) {
        if ( (!this.mandatory && !val) || this.dateRegex.test(val)) {
          this.$emit("input", val);
          this.$emit("change", val);
        }
      },
    },
    dateInput: {
      get() {
        const date = moment(this.value, "YYYY-MM-DD", true);
        return date.isValid() ? date.format(this.format) : this.value;
      },
      set(val) {
        const date = moment(val, this.format, true);
        if (!this.mandatory && !val) {
          this.$emit("input", null);
          this.$emit("change", null);
          return;
        }
        if (date.isValid()) {
          this.$emit("input", date.format("YYYY-MM-DD"));
          this.$emit("change", date.format("YYYY-MM-DD"));

        }
      },
    },
    isEditable() {
      return !this.readonly;
    },
  },
  methods: {
    close() {
      this.menu = false;
      this.dateValue = null;
    },
    isDate(val) {
      if (!val) return true;
      if (val.length < this.format.length) return true;
      // const date = moment(val,this.format, true);
      return this.dateRegex.test(val) || this.$t("invalidDateError");
    },
    isValidDate(val) {
      if (!val) return true;
      if (val.length < this.format.length) return true;
      const date = moment(val, this.format, true);
      if (!date.isValid()) return this.$t("invalidDateError");
      if (!this.futureDate && date.isAfter()) return this.$t("futureDateError");

      return true;
    },
    closeOnDubleClick(val) {
      if (this.dateValue === val || this.closeOnFirstClick) this.menu = false;
    },
  },
  mounted() {
    this.dateRules = [this.isValidDate];
  },
});
</script>

<!-- lang="i18n" WAŻNE !!! -->
<i18n lang="i18n"> 
{
  "en": {
    "close": "Close",
    "save": "Save",
    "invalidDateError": "Invalid date",
    "futureDateError": "Date cannot be in the future"
  },
  "pl": {
    "close": "Zamknij",
    "save": "Zapisz",
    "invalidDateError": "Nieprawidłowa data",
    "futureDateError": "Data nie może być datą przyszłą"
  }
}
        
</i18n>
